import { Component, OnInit, Input, OnChanges, SimpleChanges, EventEmitter, Output, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash';
import { MenuPlacement } from '../../utils/menu';
import { MenuActionBehavior } from '../../utils/menu/menu-action-behavior.enum';
import { MenuConfig } from '../../utils/menu/menu-config.interface';
import { MenuMode } from '../../utils/menu/menu-mode.enum';
import { Menu } from '../../utils/menu/menu.interface';


@Component({
  selector: 'rappider-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class RappiderMenuComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() config: MenuConfig;
  @Input() isCollapsed = false;
  @Input() isDisabled = false;
  /* coefficient value of levels paddingValue. */
  @Input() paddingValue = 24;
  @Input() menuPlacement: MenuPlacement;
  /* shows and hides labels, used to support collapsed menu */
  @Input() showLabels?: boolean = true;
  @Input() activeItem: any;
  @Input() currentSubmenuOnly = false;

  @Output() menuItemClick = new EventEmitter<Menu>();
  @Output() next = new EventEmitter<Menu>();
  @Output() hasOpenOverlaidSubmenuStatusChanged = new EventEmitter<boolean>();

  MenuMode = MenuMode;

  // To track if there is any open overlaid submenu (for horizontal menu mode)
  // Used to prevent auto collapsing when clicking inside an open submenu
  hasOpenOverlaidSubmenu = false;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.config = cloneDeep(this.config);
    this.initDefault();
    this.expandAllMenus(this.config?.items);
  }

  /* If there is menus data, calls addLevelFieldToMenus function */
  ngOnChanges(changes: SimpleChanges): void {
    this.config = cloneDeep(this.config);
    if (changes.config) {
      this.addLevelFieldToMenus(this.config?.items, 1);
      this.initDefault();
    }
    if (changes.isCollapsed) {
      this.expandAllMenus(this.config?.items);
    }
    if (changes.activeItem) {
      this.openHandler();
    }

  }
  ngAfterViewInit(): void {
    this.expandAllMenus(this.config?.items);
    this.cdr.detectChanges();
  }

  /**
   * if there is no mode, assign default mode
   *
   * @memberof RappiderMenuComponent
   */
  initDefault() {
    if (this.config?.mode == null) {
      this.config = {
        ...this.config,
        mode: MenuMode.Inline
      };
    }
    if (this.config?.backgroundColor == null) {
      this.config.backgroundColor = 'transparent';
    }
    if (!this.menuPlacement) {
      this.menuPlacement = MenuPlacement.Left;
    }
  }

  /* Adds level to input data for padding purpose. See HTML [nzPaddingLeft] */
  addLevelFieldToMenus(menu: Menu[], level: number) {
    // tslint:disable-next-line: forin
    if (menu?.length) {
      for (const key in menu) {
        if (key) {
          menu[key] = {
            ...menu[key],
            level: level,
          };
          if (menu[key].children) {
            this.addLevelFieldToMenus(menu[key].children, (level + 1));
          }
        }
      }
    }
  }

  /**
   * Get Padding Left Value
   *
   * @param {number} menuLevel
   * @return {*}
   * @memberof RappiderMenuComponent
   */
  getPaddingLeftValue(menuLevel: number) {
    switch (this.config?.mode) {
      case MenuMode.Horizontal:
        return 0;
      case MenuMode.Inline:
        return menuLevel * this.paddingValue;
      case MenuMode.Vertical:
        return 0; // menuLevel * this.paddingValue;
      default:
        return menuLevel * this.paddingValue;
    }
  }

  /* If menu-item has behavior route then navigate to url, else emits the menu item */
  menuClick(event: Event, menu: Menu) {
    event.stopPropagation();
    switch (menu.actionBehavior) {
      case MenuActionBehavior.Router:
        if (menu.redirectUrl) {
          this.router.navigate([menu.redirectUrl], { queryParams: menu.queryParams });
        }
        break;

      case MenuActionBehavior.Emit:
        this.menuItemClick.emit(menu);
        break;

      default:
        break;
    }
  }

  setMenuPlacement() {
    switch (this.menuPlacement) {
      case MenuPlacement.Center:
        return 'justify-content:center';
      case MenuPlacement.Left:
        return 'justify-content:left';
      case MenuPlacement.Right:
        return 'justify-content:right';
      default:
        return 'justify-content:left';
    }
  }

  expandAllMenus(menu: Menu[]) {
    if (menu?.length) {
      for (const item of menu) {
        if (item.children?.length) {
          // If the menu is collapsed, set isExpanded to false for child items, otherwise set isExpanded to item's isExpanded value
          item.isExpanded = this.isCollapsed ? false : item.isExpanded;
          this.expandAllMenus(item.children);
        }
      }
    }
  }

  openHandler(event?: any, menuItem?: any) {
    console.log('#### openHandler called', menuItem, event);
    // If there is a sub menu (menu items) and it has children and it is expanded and the menu mode is horizontal
    // set the status for hasOpenOverlaidSubmenu
    if (menuItem?.children?.length && menuItem?.isExpanded && this.config?.mode !== MenuMode.Inline) {
      this.hasOpenOverlaidSubmenu = true;
      this.hasOpenOverlaidSubmenuStatusChanged.emit(true);
    } else {
      this.hasOpenOverlaidSubmenu = false;
      this.hasOpenOverlaidSubmenuStatusChanged.emit(false);
    }

    if (this.activeItem && this.currentSubmenuOnly) {
      console.log('#### openHandler submenu opened: ' + this.currentSubmenuOnly);
      for (const key of this.config.items) {
        const temp = key.children.some((child: any) => child.label === this.activeItem?.item?.name);
        if (temp) {
          key.isExpanded = true;
        } else {
          key.isExpanded = false;
        }
      }
      this.getSelectedItem();
    } else {
      return null;
    }
  }

  getSelectedItem() {
    const selectedItem = document.getElementsByClassName('ant-menu-item-selected');
    if (!selectedItem[0]) {
      setTimeout(this.getSelectedItem, 100);
    } else {
      selectedItem[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  onNext(step) {
    this.next.emit(step);
  }

}
